import { lazy, Suspense, useState, DragEvent } from 'react';
import { Box } from '@mui/material';
import { useInsertNote } from '@services/note';
import type { DerivedScopeState } from '../types';
import { NoteType, PostArtifact } from '@models/note';
import { containerEditor } from '@components/richTextEditor/RichTextEditor.styles';
import { isDataTransfer } from '@utils/fileUtils';
import { Dropzone } from '@components/drawerItem/dropzone';
import FileDropWrapper from '@utils/FileDropWrapper';

const LazyRichTextEditor = lazy(() =>
  import('@components/richTextEditor/RichTextEditor').then((m) => ({ default: m.RichTextEditor })),
);

interface NoteComposerProps {
  scope: DerivedScopeState;
}

export const NoteComposer = ({ scope }: NoteComposerProps) => {
  const { insertNoteAsync, isPending: isInsertNoteLoading } = useInsertNote();

  const [draggedFiles, setDraggedFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [isEditorDialogOpen, setIsEditorDialogOpen] = useState(false);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const newDraggedFiles: File[] = [];
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i += 1) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();
          if (file !== null) {
            newDraggedFiles.push(file);
          }
        }
      }
      setDraggedFiles(newDraggedFiles);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDataTransfer(e)) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const resetDraggedFiles = () => {
    setDraggedFiles([]);
    setDragging(false);
  };

  const handleSubmit = async ({
    body,
    rawText,
    artifacts,
  }: {
    body: string;
    rawText: string;
    artifacts: PostArtifact[] | undefined;
  }) => {
    if (!rawText && (!artifacts || artifacts.length === 0)) return;
    const bodyPayload: any = {
      policyId: scope.params.policyId || undefined,
      quoteId: scope.params.quoteId || undefined,
      customerId: scope.params.customerId || undefined,
      brokerageId: scope.params.brokerageId || undefined,
      body,
      rawText,
      type: 'Note',
      isPinned: false,
    };

    if (scope.params.customerId) bodyPayload.customerId = scope.params.customerId;
    await insertNoteAsync({
      noteRequestBody: bodyPayload,
      artifacts,
    });
  };

  return (
    <Box
      sx={{
        my: 1,
        border: 'none',
        position: 'relative',
      }}
    >
      <Box
        sx={[
          containerEditor,
          {
            position: 'relative',
            display: 'flex',
            '.DraftEditor-editorContainer': {
              color: 'black',
            },
          },
          dragging
            ? {
                minHeight: 200,
              }
            : {},
        ]}
      >
        <FileDropWrapper
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          disabled={isEditorDialogOpen}
          sx={{
            width: '100%',
          }}
        >
          {dragging ? (
            <Dropzone
              sxProps={{
                dropzoneContainerSx: {
                  marginTop: 0,
                  height: '100%',
                  position: 'relative',
                },
              }}
            />
          ) : (
            <Suspense fallback={null}>
              <LazyRichTextEditor
                type={NoteType.Note as any}
                opts={{
                  overrideHandleSubmit: handleSubmit,
                  isLoading: isInsertNoteLoading,
                }}
                editConfig={{
                  quoteId: scope.params.quoteId || undefined,
                  customerId: scope.params.customerId || undefined,
                  brokerageId: scope.params.brokerageId || undefined,
                  policyId: scope.params.policyId || undefined,
                }}
                draggedFiles={draggedFiles}
                resetDraggedFiles={resetDraggedFiles}
                onDialogOpenChange={setIsEditorDialogOpen}
                hideIncludedDocumentsSection={true}
                isDisabled={isInsertNoteLoading}
              />
            </Suspense>
          )}
        </FileDropWrapper>
      </Box>
    </Box>
  );
};
